import React from 'react';
import { Cell, CellTemplate, Compatible, Uncertain, UncertainCompatible, getCellProperty, keyCodes } from '@silevis/reactgrid';

/**
 * SHER-714 — the insurer grid's Status cell: shows "Active" / "Inactive" and toggles only on an
 * explicit act (a click on the label, or Space / Enter while it has focus).
 *
 * Not a text cell driven by focus (moving onto it with Tab or an arrow key would flip it), and not
 * the stock checkbox (it shows no label, and ReactGrid merges a Delete-key or pasted text cell into
 * it as `!!value`, which would silently deactivate every selected insurer). `update` therefore only
 * accepts another statusToggle cell: Delete, paste and text fill never change the state.
 */
export interface StatusToggleCell extends Cell {
    type: 'statusToggle';
    active: boolean;
    text?: string;
}

const labelOf = (active: boolean) => (active ? 'Active' : 'Inactive');

export class StatusToggleCellTemplate implements CellTemplate<StatusToggleCell> {
    getCompatibleCell(uncertainCell: Uncertain<StatusToggleCell>): Compatible<StatusToggleCell> {
        const active = getCellProperty(uncertainCell, 'active', 'boolean');
        return { ...uncertainCell, active, text: labelOf(active), value: active ? 1 : 0 };
    }

    isFocusable(): boolean {
        return true;
    }

    update(cell: Compatible<StatusToggleCell>, cellToMerge: UncertainCompatible<StatusToggleCell>): Compatible<StatusToggleCell> {
        if (cellToMerge.type !== 'statusToggle') return cell;
        return this.getCompatibleCell({ ...cell, active: cellToMerge.active as boolean });
    }

    handleKeyDown(cell: Compatible<StatusToggleCell>, keyCode: number, ctrl: boolean, shift: boolean, alt: boolean) {
        const toggles = !cell.nonEditable && !ctrl && !shift && !alt && (keyCode === keyCodes.SPACE || keyCode === keyCodes.ENTER);
        return { cell: toggles ? this.toggled(cell) : cell, enableEditMode: false };
    }

    render(cell: Compatible<StatusToggleCell>, _isInEditMode: boolean, onCellChanged: (cell: Compatible<StatusToggleCell>, commit: boolean) => void): React.ReactNode {
        return (
            <button
                type="button"
                className={`status-toggle ${cell.active ? 'status-toggle--active' : 'status-toggle--inactive'}`}
                disabled={cell.nonEditable}
                // Keep the grid from starting a range selection on the press; the click is the act.
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onCellChanged(this.toggled(cell), true);
                }}
            >
                {cell.text}
            </button>
        );
    }

    private toggled(cell: Compatible<StatusToggleCell>): Compatible<StatusToggleCell> {
        return this.getCompatibleCell({ ...cell, active: !cell.active });
    }
}
